import React, { useEffect, useRef, useState, useCallback } from "react";
import VimeoPlayer from "react-player/vimeo";
import { useAuth } from "../../context/AuthContext";
import { SetProgressUserTaskService } from "../../services/SetProgressUserTaskService/SetProgressUserTaskService";
import { useCourseContent } from "../../context/CourseContentContext";

const PlayerComponent = ({ contentUrl, contentId, lastView, onVideoEnd, stars, progressTask, taskTitle, onProgressSent }) => {
    const { token, idUser } = useAuth();
    const { sendLog, dataCourse } = useCourseContent();
    const playerRef = useRef(null);

    const [play, setPlay] = useState(false);
    const [duration, setDuration] = useState(0);
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [lastViewLocal, setLastViewLocal] = useState(lastView);
    const [hasSeeked, setHasSeeked] = useState(false);
    const [isReady, setIsReady] = useState(false);

    const sendProgressTask = async (customProgress = null, customLastView = null) => {
        try {
            const progressToSend = customProgress !== null
                ? customProgress
                : Math.min(100, Math.max(progressTask, progressPercentage));
    
            const viewToSend = customLastView !== null
                ? customLastView
                : lastViewLocal;
    
            if (progressToSend > 0) {
                console.log(`📤 Enviando progreso: ${progressToSend}% (lastView: ${viewToSend}%)`);
                await SetProgressUserTaskService(
                    token,
                    idUser,
                    contentId,
                    progressToSend,
                    viewToSend,
                    stars
                );
                onProgressSent();
            }
        } catch (error) {
            console.error("❌ Error al enviar progreso de tarea:", error.message);
        }
    };

    const handlePlay = useCallback(() => {
        setPlay(true);
        sendLog(dataCourse?.title, taskTitle, null, null, 'play');
    }, []);

    const handlePause = () => {
        setPlay(false);

        if (!lastViewLocal) return; // No enviar si no hicimos seek
        sendProgressTask();
    };

    const handleEnded = () => {
        console.log("🎞️ The video has ended.");
        setProgressPercentage(100);
        setLastViewLocal(100);
        onVideoEnd();
        sendProgressTask(100, 100); // Forzamos los valores correctos
    };

    const handleProgress = useCallback(({ played }) => {
        const currentProgress = Math.round(played * 100);
        if (currentProgress >= 100) return;
        setLastViewLocal(currentProgress);
        setProgressPercentage(currentProgress);
    }, []);
    

    const handleDuration = (dur) => {
        setDuration(dur);
    };

    const handleReady = () => {
        console.log("🎬 Player ready");
        setIsReady(true);
    };

    // Realizar el seek una única vez cuando ready + duration + lastView disponibles
    useEffect(() => {
        if (isReady && duration > 0 && lastView > 0 && !hasSeeked && playerRef.current) {
            const secondsToSeek = (lastView / 100) * duration;
            playerRef.current.seekTo(secondsToSeek);
            setHasSeeked(true);
            setLastViewLocal(lastView);
            console.log(`✅ Seeked to ${secondsToSeek}s (${lastView}%)`);
        }
    }, [isReady, duration, lastView, hasSeeked]);

    // Visibilidad y conexión
    useEffect(() => {
        const handleVisibilityChange = () => {
            document.hidden ? handlePause() : handlePlay();
        };

        const handleOffline = () => handlePause();
        const handleOnline = () => handlePlay();

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("offline", handleOffline);
        window.addEventListener("online", handleOnline);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("online", handleOnline);
        };
    }, [handlePause, handlePlay]);

    return (
        <div className="relative pb-[56.25%] h-0 block">
            <VimeoPlayer
                ref={playerRef}
                url={contentUrl}
                className="absolute top-0 left-0 w-full h-full"
                width="100%"
                height="100%"
                playing={play}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
                onProgress={handleProgress}
                onDuration={handleDuration}
                onReady={handleReady}
                controls
                config={{
                    vimeo: {
                        playerOptions: {
                            title: false,
                            byline: false,
                            portrait: false,
                            badge: 0,
                            dnt: 1,
                        },
                    },
                }}
            />
        </div>
    );
};

export default PlayerComponent;
